import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Hash } from "@phosphor-icons/react";
import api from "@/lib/api";
import PostCard from "@/components/PostCard";
import { Stagger, StaggerItem, calmEase } from "@/components/motion/Reveal";
import { applySeo } from "@/lib/seo";

const HEADER_INITIAL = { opacity: 0, y: 32 };
const HEADER_VISIBLE = { opacity: 1, y: 0 };
const HEADER_TRANSITION = { duration: 0.7, ease: calmEase };

export default function TagPage() {
    const { tag } = useParams();
    const [posts, setPosts] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const reduceMotion = useReducedMotion();

    const current = tags.find(t => t.slug === tag);
    const name = current?.name || tag.replace(/-/g, " ");

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [p, t] = await Promise.all([api.get("/posts", { params: { tag, limit: 100 } }), api.get("/tags")]);
                setPosts(p.data || []);
                setTags(t.data || []);
            } catch { setPosts([]); }
            finally { setLoading(false); }
        })();
    }, [tag]);

    useEffect(() => {
        applySeo({
            title: `${name} — Tech News & Reviews | Tizona Tech`,
            description: `All Tizona Tech articles tagged ${name}: news, reviews, features and videos.`,
            path: `/tag/${tag}`,
            type: "website",
        });
    }, [tag, name]);

    return (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24" data-testid={`tag-page-${tag}`}>
            <motion.div className="border-b border-zinc-800 pb-10 mb-12"
                initial={reduceMotion ? false : HEADER_INITIAL} animate={HEADER_VISIBLE} transition={HEADER_TRANSITION}>
                <div className="overline mb-4">// TAGGED</div>
                <h1 className="font-display text-white text-[14vw] md:text-[8rem] leading-[0.85] tracking-tight uppercase flex items-center gap-3 break-words" data-testid="tag-page-title">
                    <Hash size={64} weight="bold" className="text-[#FFD000] shrink-0 hidden md:block" />
                    {name}<span className="text-[#FFD000]">.</span>
                </h1>
                <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500" data-testid="tag-page-count">
                    {loading ? "Loading..." : `${posts.length} ${posts.length === 1 ? "article" : "articles"}`}
                </p>
                {tags.length > 1 && <OtherTags tags={tags} active={tag} />}
            </motion.div>
            <Results loading={loading} posts={posts} />
        </div>
    );
}

function OtherTags({ tags, active }) {
    return (
        <div className="mt-8 flex flex-wrap gap-2" data-testid="tag-page-other-tags">
            {tags.slice(0, 20).map(t => (
                <Link key={t.slug} to={`/tag/${t.slug}`} data-testid={`tag-link-${t.slug}`}
                      className={`font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${
                          t.slug === active ? "bg-[#FFD000] border-[#FFD000] text-black font-bold" : "border-zinc-700 text-zinc-300 hover:border-[#FFD000] hover:text-[#FFD000]"
                      }`}>
                    #{t.name} <span className="opacity-60">{t.count}</span>
                </Link>
            ))}
        </div>
    );
}

function Results({ loading, posts }) {
    if (loading) return <div className="text-center py-16 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Loading...</div>;
    if (!posts.length) {
        return (
            <div className="text-center py-24 border border-zinc-800" data-testid="tag-page-empty">
                <div className="font-display text-3xl text-white mb-2">Nothing tagged here yet.</div>
                <Link to="/" className="text-[#FFD000] font-mono text-xs uppercase tracking-[0.25em]">Back Home</Link>
            </div>
        );
    }
    return (
        <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 border-l border-t border-zinc-800" stagger={0.08}>
            {posts.map(p => <StaggerItem key={p.id} className="bg-[#0A0A0A] border-r border-b border-zinc-800"><PostCard post={p} /></StaggerItem>)}
        </Stagger>
    );
}
